import { useEffect, useState, useCallback, useRef } from 'react';
import {
  OfflineEpisodeMeta,
  isEpisodeOffline,
  downloadEpisode,
  removeOfflineEpisode,
  OfflineDownloadAbortError,
} from '@/lib/offlineEpisodes';

export function useOfflineEpisode(episodeId: string, audioUrl?: string | null) {
  const [isOffline, setIsOffline] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [lastMeta, setLastMeta] = useState<Omit<OfflineEpisodeMeta, 'savedAt' | 'sizeBytes'> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const refresh = useCallback(async () => {
    if (!episodeId) {
      setIsOffline(false);
      return;
    }
    const offline = await isEpisodeOffline(episodeId);
    setIsOffline(offline);
  }, [episodeId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const save = useCallback(
    async (meta: Omit<OfflineEpisodeMeta, 'savedAt' | 'sizeBytes'>) => {
      if (!audioUrl) return;
      const controller = new AbortController();
      abortRef.current?.abort();
      abortRef.current = controller;
      setLastMeta(meta);
      setError(null);
      setIsDownloading(true);
      setProgress(0);
      try {
        await downloadEpisode(meta, setProgress, controller.signal);
        setIsOffline(true);
      } catch (err) {
        if (err instanceof OfflineDownloadAbortError) {
          setError(null);
        } else {
          setError(err instanceof Error ? err.message : 'Download failed');
          throw err;
        }
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
        setIsDownloading(false);
        setProgress(0);
      }
    },
    [audioUrl],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const retry = useCallback(async () => {
    if (lastMeta) await save(lastMeta);
  }, [lastMeta, save]);

  const remove = useCallback(async () => {
    if (!episodeId) return;
    await removeOfflineEpisode(episodeId);
    setIsOffline(false);
    setError(null);
  }, [episodeId]);

  return { isOffline, isDownloading, progress, error, save, cancel, retry, remove, refresh };
}
